import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import PaymentModal from "@/components/PaymentModal";
import AstroBackground from "@/components/AstroBackground";
import VastuOutstationCards from "@/components/VastuOutstationCards";
import VastuEnquiryForm from "@/components/VastuEnquiryForm";
import useSEO from "@/hooks/useSEO";
import { VASTU_RESIDENTIAL, VASTU_COMMERCIAL, VASTU_INDUSTRIAL, WHATSAPP_URL } from "@/data/constants";

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

const WHAT_YOU_RECEIVE = [
  "Full directional and zone analysis of the property",
  "Identification of Vastu defects affecting specific life areas",
  "Precise correction recommendations — without demolition where possible",
  "Colour, material, and placement guidance for each zone",
  "Entrance, kitchen, master bedroom, and wealth corner optimisation",
  "Follow-up support during implementation",
];

function PriceRow({ service, onBook }) {
  return (
    <div data-testid={`vastu-price-row-${service.id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 0", borderBottom: `1px solid ${BORDER}`, gap: "16px", flexWrap: "wrap" }}>
      <div style={{ flex: 1 }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem", fontWeight: 500, color: CHARCOAL }}>{service.name}</p>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.78rem", color: "#8A8A8A", marginTop: "4px" }}>{service.mode} · {service.location}</p>
      </div>
      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.05rem", fontWeight: 600, color: CHARCOAL, whiteSpace: "nowrap" }}>
        ₹{service.price.toLocaleString("en-IN")}
      </p>
      <button
        onClick={() => onBook(service)}
        data-testid={`vastu-book-btn-${service.id}`}
        style={{ backgroundColor: CHARCOAL, color: OFF_WHITE, padding: "10px 20px", fontFamily: "'Outfit', sans-serif", fontSize: "0.68rem", letterSpacing: "0.12em", textTransform: "uppercase", border: "none", cursor: "pointer", whiteSpace: "nowrap", transition: "background-color 0.3s" }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = GOLD; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CHARCOAL; }}
      >
        Book &amp; Pay
      </button>
    </div>
  );
}

function SubSection({ title, services, onBook }) {
  return (
    <div style={{ marginBottom: "48px" }}>
      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.2em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "0", padding: "16px 0", borderBottom: `1px solid ${BORDER}` }}>{title}</p>
      {services.map(s => <PriceRow key={s.id} service={s} onBook={onBook} />)}
    </div>
  );
}

export default function VastuPage() {
  const [selectedService, setSelectedService] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const handleBook = (s) => { setSelectedService(s); setModalOpen(true); };
  useSEO({
    title: "Vastu Shastra Consultant | Residential, Commercial & Industrial | Akkshay Narula",
    description: "Expert Vastu Shastra consultations by Akkshay Narula — residential homes, commercial offices, industrial facilities. Digital and on-site visits. Guwahati & Bangalore local, pan-India from ₹1,00,000/day, international from ₹5,00,000/day.",
    keywords: "vastu consultant India, vastu shastra for home, vastu consultant Guwahati, vastu consultant Bangalore, commercial vastu India, industrial vastu consultant, vastu for office India, vastu consultant fees, international vastu consultant",
    canonical: "/services/vastu",
  });

  return (
    <main style={{ backgroundColor: OFF_WHITE }} data-testid="vastu-page">
      {/* Hero */}
      <section style={{ backgroundColor: CHARCOAL, padding: "80px 0", position: "relative", overflow: "hidden" }}>
        <AstroBackground count={12} />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16" style={{ position: "relative", zIndex: 1 }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#6A6A6A", fontFamily: "'Outfit', sans-serif", fontSize: "0.68rem", letterSpacing: "0.12em", textTransform: "uppercase", textDecoration: "none", marginBottom: "36px" }}>
            <ArrowLeft size={11} /> Home
          </Link>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>Space & Energy</p>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "20px" }}>
            <span style={{ fontFamily: "serif", fontSize: "4.5rem", color: GOLD, opacity: 0.22, lineHeight: 1, flexShrink: 0, marginTop: "6px" }}>⊕</span>
            <div>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, color: OFF_WHITE, lineHeight: 1.1, marginBottom: "16px" }}>
                Vastu Shastra<br />Consultation
              </h1>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#8A8A8A", lineHeight: 1.75, maxWidth: "540px" }}>
                Ancient spatial science for homes, offices, and industrial facilities — aligning architecture with cosmic energy.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "40px", marginTop: "40px", flexWrap: "wrap" }}>
            {[{ label: "Formats", value: "Digital & On-Site" }, { label: "Scope", value: "Residential · Commercial · Industrial" }, { label: "Starting From", value: "₹5,100" }].map(item => (
              <div key={item.label}>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.58rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#4A4A4A", marginBottom: "5px" }}>{item.label}</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: OFF_WHITE, fontWeight: 500 }}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section style={{ padding: "80px 0", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>About This Service</p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3vw, 2.4rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "24px" }}>What is Vastu Shastra?</h2>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", lineHeight: 1.85, marginBottom: "18px" }}>
                Vastu Shastra is the ancient Indian science of spatial energy and architecture. Based on the principle that every structure has an energy field which profoundly influences the health, prosperity, and harmony of its occupants, Vastu maps directional zones to specific life areas and prescribes precise spatial corrections.
              </p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", lineHeight: 1.85 }}>
                With over a decade of practice across residential homes, corporate offices, and industrial facilities, Akkshay Narula delivers Vastu consultations that focus on practical, implementable corrections — without unnecessary structural changes where possible.
              </p>
            </div>
            <div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>What You'll Receive</p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
                {WHAT_YOU_RECEIVE.map((item, i) => (
                  <li key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start", fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#4A4A4A", lineHeight: 1.65 }}>
                    <span style={{ color: GOLD, fontFamily: "serif", fontSize: "0.9rem", lineHeight: 1.75, flexShrink: 0 }}>—</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: "80px 0" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>Fees</p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3vw, 2.4rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "8px" }}>Consultation Fees — Guwahati & Bengaluru</h2>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.85rem", color: "#8A8A8A", marginBottom: "48px" }}>100% advance payment. All fees are fixed and non-negotiable.</p>
          <SubSection title="Residential Vastu" services={VASTU_RESIDENTIAL} onBook={handleBook} />
          <SubSection title="Commercial Vastu" services={VASTU_COMMERCIAL} onBook={handleBook} />
          <SubSection title="Industrial & Factory Vastu" services={VASTU_INDUSTRIAL} onBook={handleBook} />
        </div>
      </section>

      {/* Outstation & International */}
      <section style={{ padding: "72px 0", backgroundColor: "#F5F3EB", borderTop: `1px solid ${BORDER}` }} data-testid="vastu-outstation-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>Travel & On-Site Visits</p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3vw, 2.4rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "40px" }}>Outstation & International Vastu</h2>
          <VastuOutstationCards />
        </div>
      </section>

      {/* Vastu Enquiry */}
      <section style={{ padding: "72px 0", borderTop: `1px solid ${BORDER}` }} data-testid="vastu-enquiry-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>Project Enquiry</p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3vw, 2.4rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "40px" }}>Vastu Project Enquiry</h2>
          <VastuEnquiryForm />
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: CHARCOAL, padding: "72px 0", position: "relative", overflow: "hidden" }}>
        <AstroBackground count={8} />
        <div className="max-w-2xl mx-auto px-6 text-center" style={{ position: "relative", zIndex: 1 }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, color: OFF_WHITE, marginBottom: "16px" }}>Ready to Begin?</h2>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#8A8A8A", marginBottom: "32px", lineHeight: 1.7 }}>Select a service above to book, or message on WhatsApp to discuss your requirements first.</p>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
            style={{ backgroundColor: GOLD, color: CHARCOAL, padding: "15px 36px", fontFamily: "'Outfit', sans-serif", fontSize: "0.75rem", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "8px" }}>
            Request via WhatsApp <ArrowRight size={13} />
          </a>
        </div>
      </section>

      <PaymentModal service={selectedService} open={modalOpen} onClose={() => setModalOpen(false)} />
    </main>
  );
}
